import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import type { Selection, Key } from "react-aria-components";
import {
  BatchActionBar,
  BatchActionButton,
} from "@/components/batch-action-bar";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useDebounce } from "@/hooks/use-debounce";
import { usePrefetchOnHover } from "@/hooks/use-prefetch";
import { STALE_TIMES } from "@/lib/query-performance";
import {
  AriaGridList,
  AriaGridListItem,
  AriaGridListCheckbox,
  AriaGridListAction,
} from "@/components/ui/aria-gridlist";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { AuthorityBadge } from "@/components/authority-badge";
import {
  authorityConfig,
  authorityLevelsSorted,
  type AuthorityLevel,
  type SourceType,
  type SourceStatus,
} from "@iamu/shared";
import { SortDropdown } from "@/components/ui/sort-dropdown.js";
import { TableToolbar } from "@/components/ui/table-toolbar";
import { TableSearchInput } from "@/components/ui/table-search-input";
import { FilterPill } from "@/components/ui/filter-bar";
import { FilteredEmptyState } from "@/components/ui/filtered-empty-state";
import { formatRelativeTime } from "@/lib/freshness";
import { Timestamp } from "@/components/ui/timestamp";
import { toast } from "sonner";
import {
  Globe,
  FileText,
  PenLine,
  Palette,
  Loader2,
  Search,
  MoreHorizontal,
  Trash2,
  ShieldCheck,
  RefreshCw,
  ExternalLink,
  Copy,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  Database,
  Plus,
  RotateCcw,
} from "lucide-react";
import { AddSourceDialog } from "@/components/add-source-dialog";
import { Compass } from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { useCanEdit, useEditorCopy } from "@/lib/project-access";
import {
  statusColors,
  statusLabels,
  statusDotColors,
} from "@/components/source-status";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SourceRow {
  id: string;
  name: string;
  sourceType: string;
  category: string | null;
  authorityLevel: string;
  status: string;
  language: string | null;
  url: string | null;
  updatedAt: string;
  createdAt: string;
  lastCrawledAt: string | null;
  crawlSchedule: string | null;
  metadata: unknown;
  verified: boolean;
  errorMessage: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Source status colors, labels, and dot colors imported from shared component

const ALL_STATUSES: SourceStatus[] = [
  "pending",
  "processing",
  "ready",
  "ready_unverified",
  "error",
  "stale",
];

const sourceTypeIcons = {
  web: Globe,
  document: FileText,
  manual: PenLine,
} as const;

function getSourceIcon(source: {
  sourceType: string;
  category: string | null;
}) {
  if (source.category === "design_system") return Palette;
  return (
    sourceTypeIcons[source.sourceType as keyof typeof sourceTypeIcons] ??
    FileText
  );
}

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Newest first" },
  { value: "createdAt:asc", label: "Oldest first" },
  { value: "name:asc", label: "Name A\u2013Z" },
  { value: "name:desc", label: "Name Z\u2013A" },
  { value: "authorityLevel:desc", label: "Authority (highest)" },
  { value: "authorityLevel:asc", label: "Authority (lowest)" },
  { value: "updatedAt:desc", label: "Recently updated" },
] as const;

// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------

/** Convert React Aria Selection to an array of string IDs. */
function selectionToIds(selection: Selection): string[] {
  if (selection === "all") return [];
  return [...selection].map(String);
}

// ---------------------------------------------------------------------------
// Status Summary Bar
// ---------------------------------------------------------------------------

function StatusSummaryBar({
  items,
  activeStatus,
  onStatusClick,
}: {
  items: SourceRow[];
  activeStatus: string;
  onStatusClick: (status: string) => void;
}) {
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of ALL_STATUSES) map[s] = 0;
    for (const item of items) map[item.status] = (map[item.status] ?? 0) + 1;
    return map;
  }, [items]);

  const hasAny = items.length > 0;
  if (!hasAny) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Filter by status"
    >
      {ALL_STATUSES.map((status) => {
        const count = counts[status] ?? 0;
        if (count === 0) return null;
        const isActive = activeStatus === status;
        return (
          <button
            key={status}
            type="button"
            onClick={() => onStatusClick(isActive ? "all" : status)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
              isActive
                ? "bg-foreground text-background"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            aria-pressed={isActive}
            aria-label={`${statusLabels[status]}: ${count} source${count !== 1 ? "s" : ""}`}
          >
            <span
              className={`size-1.5 rounded-full ${statusDotColors[status]}`}
            />
            {statusLabels[status]}
            <span className="tabular-nums">{count}</span>
          </button>
        );
      })}
      {activeStatus !== "all" && (
        <button
          type="button"
          onClick={() => onStatusClick("all")}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground ml-1"
          aria-label="Clear status filter"
        >
          <X className="size-3" />
          Clear
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Filter Chips
// ---------------------------------------------------------------------------

function FilterChips({
  search,
  statusFilter,
  authorityFilter,
  typeFilter,
  onClearSearch,
  onClearStatus,
  onClearAuthority,
  onClearType,
  onClearAll,
}: {
  search: string;
  statusFilter: string;
  authorityFilter: string;
  typeFilter: string;
  onClearSearch: () => void;
  onClearStatus: () => void;
  onClearAuthority: () => void;
  onClearType: () => void;
  onClearAll: () => void;
}) {
  const chips: { label: string; onClear: () => void }[] = [];

  if (search)
    chips.push({ label: `Search: "${search}"`, onClear: onClearSearch });
  if (statusFilter !== "all")
    chips.push({
      label: `Status: ${statusLabels[statusFilter] ?? statusFilter}`,
      onClear: onClearStatus,
    });
  if (authorityFilter !== "all")
    chips.push({
      label: `Authority: ${authorityConfig[authorityFilter as AuthorityLevel]?.label ?? authorityFilter}`,
      onClear: onClearAuthority,
    });
  if (typeFilter !== "all")
    chips.push({ label: `Type: ${typeFilter}`, onClear: onClearType });

  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Active filters"
    >
      {chips.map((chip) => (
        <Badge
          key={chip.label}
          variant="secondary"
          className="bg-primary/10 text-primary-ink gap-1"
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onClear}
            className="ml-0.5 rounded-sm hover:bg-primary/20 p-0.5"
            aria-label={`Remove filter: ${chip.label}`}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      {chips.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Batch Action Toolbar
// ---------------------------------------------------------------------------

function BatchToolbar({
  selectedCount,
  selectedRows,
  onDelete,
  onVerify,
  onRecrawl,
  onClearSelection,
  isDeleting,
  isVerifying,
  isRecrawling,
}: {
  selectedCount: number;
  selectedRows: SourceRow[];
  onDelete: () => void;
  onVerify: () => void;
  onRecrawl: () => void;
  onClearSelection: () => void;
  isDeleting: boolean;
  isVerifying: boolean;
  isRecrawling: boolean;
}) {
  const hasUnverified = selectedRows.some(
    (r) => r.status === "ready_unverified",
  );
  const hasWeb = selectedRows.some((r) => r.sourceType === "web");
  const isBusy = isDeleting || isVerifying || isRecrawling;

  return (
    <BatchActionBar
      count={selectedCount}
      noun="source"
      onClear={onClearSelection}
    >
      {hasUnverified && (
        <BatchActionButton onClick={onVerify} disabled={isBusy}>
          {isVerifying ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <ShieldCheck className="size-3" />
          )}
          Verify
        </BatchActionButton>
      )}
      {hasWeb && (
        <BatchActionButton onClick={onRecrawl} disabled={isBusy}>
          {isRecrawling ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <RefreshCw className="size-3" />
          )}
          Recrawl
        </BatchActionButton>
      )}
      <BatchActionButton
        onClick={onDelete}
        disabled={isBusy}
        className="text-primary-foreground"
      >
        {isDeleting ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Trash2 className="size-3" />
        )}
        Delete
      </BatchActionButton>
    </BatchActionBar>
  );
}

// ---------------------------------------------------------------------------
// Row Actions Menu
// ---------------------------------------------------------------------------

function RowActions({
  source,
  onDelete,
  onVerify,
}: {
  source: SourceRow;
  onDelete: (id: string) => void;
  onVerify: (id: string) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Actions for ${source.name}`}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {source.status === "ready_unverified" && (
          <>
            <DropdownMenuItem onClick={() => onVerify(source.id)}>
              <ShieldCheck className="size-4" />
              Verify
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          variant="destructive"
          onClick={() => onDelete(source.id)}
        >
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

function PaginationControls({
  page,
  pageCount,
  totalCount,
  pageSize,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalCount);

  return (
    <div
      className="flex items-center justify-between"
      role="navigation"
      aria-label="Pagination"
    >
      <p className="text-sm text-muted-foreground tabular-nums">
        {totalCount === 0
          ? "No sources"
          : `Showing ${from}\u2013${to} of ${totalCount}`}
      </p>
      {pageCount > 1 && (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-xs"
            onClick={() => onPageChange(0)}
            disabled={page === 0}
            aria-label="First page"
          >
            <ChevronsLeft className="size-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon-xs"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          <span className="text-sm text-muted-foreground px-2 tabular-nums">
            {page + 1} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="icon-xs"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount - 1}
            aria-label="Next page"
          >
            <ChevronRight className="size-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon-xs"
            onClick={() => onPageChange(pageCount - 1)}
            disabled={page >= pageCount - 1}
            aria-label="Last page"
          >
            <ChevronsRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function SourcesAuthorityStrip({ items }: { items: SourceRow[] }) {
  if (items.length === 0) return null;

  const total = items.length;
  const hardConstraints = items.filter(
    (s) =>
      s.authorityLevel === "legislation" || s.authorityLevel === "regulation",
  ).length;
  const standardsAndPolicies = items.filter(
    (s) =>
      s.authorityLevel === "standard" || s.authorityLevel === "company_policy",
  ).length;
  const unverified = items.filter(
    (s) => s.status === "ready_unverified",
  ).length;
  const errors = items.filter((s) => s.status === "error").length;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
        <span className="text-xs text-muted-foreground">Total Sources</span>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums text-foreground">
            {total}
          </span>
          <span className="text-xs text-muted-foreground">
            in knowledge base
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
        <span className="text-xs text-muted-foreground">Hard Constraints</span>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums text-foreground">
            {hardConstraints}
          </span>
          <span className="text-xs text-muted-foreground">legally binding</span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
        <span className="text-xs text-muted-foreground">
          Standards & Policies
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums text-foreground">
            {standardsAndPolicies}
          </span>
          <span className="text-xs text-muted-foreground">frameworks</span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3.5 space-y-1">
        <span className="text-xs text-muted-foreground">Ingestion Health</span>
        <div className="flex items-baseline gap-2">
          {errors > 0 ? (
            <>
              <span className="text-xl font-semibold tabular-nums text-severity-critical">
                {errors}
              </span>
              <span className="text-xs text-severity-critical">
                sync errors
              </span>
            </>
          ) : unverified > 0 ? (
            <>
              <span className="text-xl font-semibold tabular-nums text-severity-important">
                {unverified}
              </span>
              <span className="text-xs text-severity-important">
                need verification
              </span>
            </>
          ) : (
            <>
              <span className="text-xl font-semibold tabular-nums text-finding-helps">
                100%
              </span>
              <span className="text-xs text-finding-helps">healthy</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source Status Indicator
// ---------------------------------------------------------------------------

function SourceStatusIndicator({ source }: { source: SourceRow }) {
  const processingStep = (source.metadata as Record<string, unknown>)
    ?.processingStep as string | undefined;

  if (source.status === "processing") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
        <Loader2 className="size-3 animate-spin shrink-0" aria-hidden="true" />
        <span className="truncate">
          {processingStep
            ? `${processingStep.charAt(0).toUpperCase()}${processingStep.slice(1)}...`
            : "Processing..."}
        </span>
      </span>
    );
  }

  if (source.status === "error") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="secondary"
            tabIndex={0}
            className={cn(
              statusColors.error,
              "cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            )}
          >
            <AlertCircle className="size-3 shrink-0" aria-hidden="true" />
            Error
          </Badge>
        </TooltipTrigger>
        {source.errorMessage && (
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-xs">{source.errorMessage}</p>
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  if (source.status === "ready_unverified") {
    return (
      <Badge variant="secondary" className={statusColors.ready_unverified}>
        <ShieldCheck className="size-3 shrink-0" aria-hidden="true" />
        Unverified
      </Badge>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span
        className={`size-1.5 rounded-full shrink-0 ${statusDotColors[source.status] ?? "bg-muted-foreground"}`}
      />
      <span className="capitalize">
        {statusLabels[source.status] ?? source.status}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Confirm Dialog
// ---------------------------------------------------------------------------

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  isLoading,
  variant = "destructive",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  isLoading: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button variant={variant} onClick={onConfirm} disabled={isLoading}>
            {isLoading && <Loader2 className="size-3.5 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Source List Item (with hover prefetch)
// ---------------------------------------------------------------------------

function SourceListItemContent({
  source,
  selectable,
}: {
  source: SourceRow;
  selectable: boolean;
}) {
  const utils = trpc.useUtils();
  const prefetch = usePrefetchOnHover(() =>
    utils.source.getById.prefetch({ id: source.id }),
  );
  const Icon = getSourceIcon(source);

  return (
    <div className="flex flex-1 items-center gap-3 min-w-0" {...prefetch}>
      {selectable && <AriaGridListCheckbox />}
      <div className="flex size-7 shrink-0 items-center justify-center rounded border border-border bg-muted/40 text-muted-foreground">
        <Icon className="size-3.5" />
      </div>
      <div className="flex-1 min-w-0 pr-2">
        <div className="text-sm font-semibold tracking-tight text-foreground truncate">
          {source.name}
        </div>
        {source.url ? (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground truncate max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate">
              {source.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground capitalize">
            {source.category
              ? source.category.replace(/_/g, " ")
              : source.sourceType}
          </span>
        )}
      </div>

      {/* Authority Column */}
      <div className="w-36 shrink-0 hidden sm:flex items-center">
        <AuthorityBadge level={source.authorityLevel} />
      </div>

      {/* Type Column */}
      <div className="w-32 shrink-0 hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="capitalize">{source.sourceType}</span>
        {source.language && (
          <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            {source.language.slice(0, 2).toLowerCase()}
          </span>
        )}
      </div>

      {/* Last Sync Column */}
      <div className="w-36 shrink-0 hidden lg:flex flex-col text-xs text-muted-foreground">
        {source.lastCrawledAt ? (
          <Timestamp date={source.lastCrawledAt}>
            Synced {formatRelativeTime(new Date(source.lastCrawledAt))}
          </Timestamp>
        ) : (
          <Timestamp date={source.updatedAt}>
            Added {formatRelativeTime(new Date(source.updatedAt))}
          </Timestamp>
        )}
      </div>

      {/* Status Column */}
      <div className="w-24 shrink-0 flex items-center justify-end">
        <SourceStatusIndicator source={source} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content Search Panel (crawler semantic search)
// ---------------------------------------------------------------------------

function ContentSearchPanel({
  projectId,
  sourceDetailPath,
}: {
  projectId: string;
  sourceDetailPath: (id: string) => string;
}) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");

  const { data, isLoading } = trpc.source.searchContent.useQuery(
    { projectId, query, limit: 10 },
    { enabled: !!query },
  );

  if (!query && !searchInput) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = searchInput.trim();
          if (trimmed) setQuery(trimmed);
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Database className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search ingested content..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 pl-8 text-sm"
            aria-label="Search ingested content"
          />
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = searchInput.trim();
          if (trimmed) setQuery(trimmed);
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Database className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder="Search ingested content..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-9 pl-8 text-sm"
            aria-label="Search ingested content"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setQuery("");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear content search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Button type="submit" size="sm" disabled={!searchInput.trim()}>
          Search
        </Button>
      </form>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      )}

      {query && !isLoading && (!data || data.length === 0) && (
        <p className="text-muted-foreground py-4 text-center text-sm">
          No content matches &ldquo;{query}&rdquo;.
        </p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-1.5">
          {data.map((hit) => (
            <button
              key={hit.id}
              type="button"
              onClick={() => {
                if (hit.sourceId) navigate(sourceDetailPath(hit.sourceId));
              }}
              className="bg-card flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left shadow-[0_1px_4px_rgba(0,0,0,0.04)] hover:bg-accent/30 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {hit.section && (
                    <span className="text-sm font-semibold tracking-tight truncate">
                      {hit.section}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto shrink-0">
                    {Math.round(hit.score * 100)}%
                  </span>
                </div>
                <p className="text-sm text-foreground line-clamp-2 mt-0.5">
                  {hit.content}
                </p>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {hit.sourceName}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SourcesPage() {
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const emptyDescription = useEditorCopy(
    "Sources are documents, web pages, and regulations that form your knowledge base.",
    "Sources are documents, web pages, and regulations that form your knowledge base. Ask an editor to add one.",
  );
  const utils = trpc.useUtils();
  const [searchParams] = useSearchParams();
  const { id: projectId } = useParams<{ id: string }>();
  const sourceDetailPath = (sourceId: string) =>
    projectId
      ? `/projects/${projectId}/sources/${sourceId}`
      : `/sources/${sourceId}`;

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);
  const [statusFilter, setStatusFilter] = useState(() => {
    const initial = searchParams.get("status") ?? "all";
    return ALL_STATUSES.includes(initial as SourceStatus) ? initial : "all";
  });
  const [authorityFilter, setAuthorityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showContentSearch, setShowContentSearch] = useState(false);

  // Sorting
  const [sortKey, setSortKey] = useState("createdAt:desc");
  const sortBy = sortKey.split(":")[0] as
    | "name"
    | "authorityLevel"
    | "updatedAt"
    | "createdAt";
  const sortOrder = sortKey.split(":")[1] as "asc" | "desc";

  // Pagination
  const [page, setPage] = useState(0);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearch,
    statusFilter,
    authorityFilter,
    typeFilter,
    categoryFilter,
  ]);

  // Selection (React Aria)
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  // Dialogs
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[]>([]);

  // Data query — staleTime prevents refetch on back-navigation
  const { data, isLoading, error, refetch } = trpc.source.list.useQuery(
    {
      projectId,
      status:
        statusFilter === "all" ? undefined : (statusFilter as SourceStatus),
      search: debouncedSearch || undefined,
      authorityLevel:
        authorityFilter === "all"
          ? undefined
          : (authorityFilter as AuthorityLevel),
      sourceType: typeFilter === "all" ? undefined : (typeFilter as SourceType),
      category:
        categoryFilter === "design_system"
          ? ("design_system" as const)
          : undefined,
      sortBy,
      sortOrder,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    {
      staleTime: STALE_TIMES.list,
      refetchInterval: (query) => {
        const hasProcessing = query.state.data?.items?.some(
          (s) => s.status === "processing",
        );
        return hasProcessing ? 3000 : false;
      },
    },
  );

  // Also fetch unfiltered data for status summary counts
  const { data: allData } = trpc.source.list.useQuery(
    {
      projectId,
      search: debouncedSearch || undefined,
      authorityLevel:
        authorityFilter === "all"
          ? undefined
          : (authorityFilter as AuthorityLevel),
      sourceType: typeFilter === "all" ? undefined : (typeFilter as SourceType),
      category:
        categoryFilter === "design_system"
          ? ("design_system" as const)
          : undefined,
      sortBy: "createdAt",
      sortOrder: "desc",
      limit: 100,
      offset: 0,
    },
    {
      refetchInterval: (query) => {
        const hasProcessing = query.state.data?.items?.some(
          (s) => s.status === "processing",
        );
        return hasProcessing ? 3000 : false;
      },
    },
  );

  const sources = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Batch mutations
  const deleteManyMutation = trpc.source.deleteMany.useMutation({
    onSuccess: (result) => {
      toast.success(
        `Deleted ${result.deletedCount} source${result.deletedCount !== 1 ? "s" : ""}`,
      );
      setSelectedKeys(new Set());
      setDeleteDialogOpen(false);
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to delete sources"),
  });

  const verifyManyMutation = trpc.source.verifyMany.useMutation({
    onSuccess: (result) => {
      toast.success(
        `Verified ${result.updatedCount} source${result.updatedCount !== 1 ? "s" : ""}`,
      );
      setSelectedKeys(new Set());
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to verify sources"),
  });

  const recrawlManyMutation = trpc.source.recrawlMany.useMutation({
    onSuccess: (result) => {
      toast.success(
        `Enqueued ${result.enqueuedCount} recrawl${result.enqueuedCount !== 1 ? "s" : ""}`,
      );
      setSelectedKeys(new Set());
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to enqueue recrawls"),
  });

  // Single-item mutations for inline actions
  const deleteSingleMutation = trpc.source.delete.useMutation({
    onSuccess: () => {
      toast.success("Source deleted");
      setDeleteDialogOpen(false);
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to delete source"),
  });

  const verifySingleMutation = trpc.source.verifyMany.useMutation({
    onSuccess: () => {
      toast.success("Source verified");
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to verify source"),
  });

  const recrawlSingleMutation = trpc.source.recrawl.useMutation({
    onSuccess: () => {
      toast.success("Recrawl enqueued");
      utils.source.list.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to enqueue recrawl"),
  });

  // Handlers
  const selectedIds = useMemo(
    () => selectionToIds(selectedKeys),
    [selectedKeys],
  );

  const handleDeleteSingle = useCallback((id: string) => {
    setDeleteTargetIds([id]);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteBatch = useCallback(() => {
    setDeleteTargetIds(selectedIds);
    setDeleteDialogOpen(true);
  }, [selectedIds]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteTargetIds.length === 1) {
      deleteSingleMutation.mutate({ id: deleteTargetIds[0] });
    } else {
      deleteManyMutation.mutate({ ids: deleteTargetIds });
    }
  }, [deleteTargetIds, deleteSingleMutation, deleteManyMutation]);

  const handleVerifySingle = useCallback(
    (id: string) => {
      verifySingleMutation.mutate({ ids: [id] });
    },
    [verifySingleMutation],
  );

  const handleVerifyBatch = useCallback(() => {
    verifyManyMutation.mutate({ ids: selectedIds });
  }, [selectedIds, verifyManyMutation]);

  const handleRecrawlSingle = useCallback(
    (id: string) => {
      recrawlSingleMutation.mutate({ id });
    },
    [recrawlSingleMutation],
  );

  const handleRecrawlBatch = useCallback(() => {
    recrawlManyMutation.mutate({ ids: selectedIds });
  }, [selectedIds, recrawlManyMutation]);

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    setStatusFilter("all");
    setAuthorityFilter("all");
    setTypeFilter("all");
    setCategoryFilter("all");
  }, []);

  const selectedRows = useMemo(
    () =>
      selectedIds
        .map((id) => sources.find((s) => s.id === id))
        .filter(Boolean) as SourceRow[],
    [selectedIds, sources],
  );

  const deleteDialogSource =
    deleteTargetIds.length === 1
      ? sources.find((s) => s.id === deleteTargetIds[0])
      : null;

  // Clear selection when data changes
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [debouncedSearch, statusFilter, authorityFilter, typeFilter, page]);

  const hasActiveFilters = Boolean(
    debouncedSearch ||
      statusFilter !== "all" ||
      authorityFilter !== "all" ||
      typeFilter !== "all",
  );

  // Navigation handler for AriaGridList
  const handleAction = useCallback(
    (key: Key) => {
      navigate(sourceDetailPath(String(key)));
    },
    [navigate, projectId],
  );

  return (
    <Page
      title="Sources"
      actions={
        canEdit ? (
          <ActionBar
            primary={
              projectId ? (
                <Button
                  onClick={() =>
                    navigate(`/projects/${projectId}/sources/discover`)
                  }
                >
                  <Compass className="size-4" />
                  Discover
                </Button>
              ) : undefined
            }
            secondary={[
              <Button key="add-source" onClick={() => setAddSourceOpen(true)}>
                <Plus className="size-4" />
                Add source
              </Button>,
            ]}
          />
        ) : undefined
      }
    >
      {/* Domain Authority Summary Strip */}
      <SourcesAuthorityStrip
        items={((allData?.items ?? sources) as SourceRow[]) ?? []}
      />

      {/* Search + Filters Toolbar */}
      <TableToolbar
        hasActiveFilters={hasActiveFilters}
        onResetFilters={clearAllFilters}
        count={
          totalCount !== undefined ? (
            <span>
              <strong className="text-foreground">{totalCount}</strong> source
              {totalCount !== 1 ? "s" : ""}
            </span>
          ) : undefined
        }
        actions={
          <>
            <SortDropdown
              options={SORT_OPTIONS}
              value={sortKey}
              onChange={(v) => {
                setSortKey(v);
                setPage(0);
              }}
            />
            {projectId && (
              <Button
                variant={showContentSearch ? "secondary" : "outline"}
                size="sm"
                className="h-9 gap-1.5 text-xs rounded-full"
                onClick={() => setShowContentSearch((prev) => !prev)}
              >
                <Database className="size-3.5" />
                Search chunks
              </Button>
            )}
          </>
        }
      >
        <TableSearchInput
          placeholder="Search sources..."
          value={searchInput}
          onChange={setSearchInput}
          ariaLabel="Search sources by name"
        />

        <FilterPill
          ariaLabel="Filter by authority level"
          value={authorityFilter}
          onValueChange={setAuthorityFilter}
          options={[
            { value: "all", label: "All levels" },
            ...authorityLevelsSorted.map((a) => ({
              value: a,
              label: authorityConfig[a].label,
            })),
          ]}
        />

        <FilterPill
          ariaLabel="Filter by source type"
          value={
            typeFilter === "all" && categoryFilter === "design_system"
              ? "design_system"
              : typeFilter
          }
          onValueChange={(v) => {
            if (v === "design_system") {
              setTypeFilter("all");
              setCategoryFilter("design_system");
            } else {
              setTypeFilter(v);
              setCategoryFilter("all");
            }
          }}
          options={[
            { value: "all", label: "All types" },
            { value: "web", label: "Web" },
            { value: "document", label: "Document" },
            { value: "manual", label: "Manual" },
            { value: "design_system", label: "Design system" },
          ]}
        />
      </TableToolbar>

      {/* Semantic Content Search Panel (Collapsible Drawer) */}
      {showContentSearch && projectId && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Database className="size-3.5 text-primary" />
              Semantic Chunk Search
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setShowContentSearch(false)}
              aria-label="Close semantic search"
            >
              <X className="size-3.5" />
            </Button>
          </div>
          <ContentSearchPanel
            projectId={projectId}
            sourceDetailPath={sourceDetailPath}
          />
        </div>
      )}

      {/* Active filter chips */}
      <FilterChips
        search={debouncedSearch}
        statusFilter={statusFilter}
        authorityFilter={authorityFilter}
        typeFilter={typeFilter}
        onClearSearch={() => setSearchInput("")}
        onClearStatus={() => setStatusFilter("all")}
        onClearAuthority={() => setAuthorityFilter("all")}
        onClearType={() => setTypeFilter("all")}
        onClearAll={clearAllFilters}
      />

      {/* Status summary bar (uses unfiltered-by-status data) */}
      <StatusSummaryBar
        items={(allData?.items ?? []) as SourceRow[]}
        activeStatus={statusFilter}
        onStatusClick={setStatusFilter}
      />

      {/* Batch action toolbar */}
      {canEdit && (
        <BatchToolbar
          selectedCount={selectedIds.length}
          selectedRows={selectedRows}
          onDelete={handleDeleteBatch}
          onVerify={handleVerifyBatch}
          onRecrawl={handleRecrawlBatch}
          onClearSelection={() => setSelectedKeys(new Set())}
          isDeleting={deleteManyMutation.isPending}
          isVerifying={verifyManyMutation.isPending}
          isRecrawling={recrawlManyMutation.isPending}
        />
      )}

      {/* Content */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-lg px-6 py-5 shadow-[0_1px_4px_rgba(0,0,0,0.04)]"
            >
              <Skeleton className="h-4 w-2/5 mb-2" />
              <Skeleton className="h-3 w-1/3 mb-2" />
              <Skeleton className="h-3 w-1/4" />
            </div>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Failed to load sources</p>
              <p className="text-xs text-muted-foreground">
                {error.message || "An error occurred while loading sources"}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : !sources.length ? (
        hasActiveFilters ? (
          <FilteredEmptyState
            title="No sources match your filters"
            description="No sources match your active search query or filter selection."
            onReset={clearAllFilters}
            resetLabel="Clear all filters"
          />
        ) : (
          <EmptyState
            icon={Database}
            title="No sources yet"
            description={emptyDescription}
            actions={
              canEdit && (
                <>
                  {projectId && (
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(`/projects/${projectId}/sources/discover`)
                      }
                    >
                      <Compass className="size-4" />
                      Discover sources
                    </Button>
                  )}
                  <Button onClick={() => setAddSourceOpen(true)}>
                    <Plus className="size-4" />
                    Add source
                  </Button>
                </>
              )
            }
          />
        )
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground select-none">
            {canEdit && <div className="w-5 shrink-0" />}
            <div className="size-7 shrink-0" />
            <div className="flex-1 min-w-0">Source</div>
            <div className="w-36 shrink-0 hidden sm:block">Authority</div>
            <div className="w-32 shrink-0 hidden md:block">Type</div>
            <div className="w-36 shrink-0 hidden lg:block">Last Sync</div>
            <div className="w-24 shrink-0 text-right">Status</div>
            {canEdit && <div className="w-8 shrink-0" />}
          </div>

          <AriaGridList
            aria-label="Sources"
            selectionMode={canEdit ? "multiple" : "none"}
            selectionBehavior="toggle"
            selectedKeys={selectedKeys}
            onSelectionChange={setSelectedKeys}
            onAction={handleAction}
            className="border-none divide-y divide-border"
          >
            {sources.map((source) => {
              const s = source as SourceRow;
              return (
                <AriaGridListItem
                  key={s.id}
                  id={s.id}
                  textValue={s.name}
                  className="px-4 py-2.5"
                >
                  <div className="flex items-center gap-2 w-full">
                    <SourceListItemContent source={s} selectable={canEdit} />
                    {canEdit && (
                      <AriaGridListAction aria-label={`Actions for ${s.name}`}>
                        <RowActions
                          source={s}
                          onDelete={handleDeleteSingle}
                          onVerify={handleVerifySingle}
                        />
                      </AriaGridListAction>
                    )}
                  </div>
                </AriaGridListItem>
              );
            })}
          </AriaGridList>

          <div className="border-t border-border bg-muted/10 px-4 py-2.5">
            <PaginationControls
              page={page}
              pageCount={pageCount}
              totalCount={totalCount}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      {/* Add source dialog */}
      {canEdit && (
        <AddSourceDialog open={addSourceOpen} onOpenChange={setAddSourceOpen} />
      )}

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetIds([]);
          setDeleteDialogOpen(open);
        }}
        title={
          deleteTargetIds.length === 1
            ? "Delete source"
            : `Delete ${deleteTargetIds.length} sources`
        }
        description={
          deleteTargetIds.length === 1
            ? `This will permanently delete "${deleteDialogSource?.name ?? ""}" and all its versions and chunks. This cannot be undone.`
            : `This will permanently delete ${deleteTargetIds.length} sources and all their versions and chunks. This cannot be undone.`
        }
        confirmLabel={
          deleteTargetIds.length === 1
            ? "Delete"
            : `Delete ${deleteTargetIds.length}`
        }
        onConfirm={handleConfirmDelete}
        isLoading={
          deleteSingleMutation.isPending || deleteManyMutation.isPending
        }
      />
    </Page>
  );
}
